import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { supabaseClient } from '../../services/supabase';
import './Notifications.css';

interface Notification {
  id: string;
  parent_id: string;
  content: string;
  created_at: string;
  chart_context?: string;
  location_context?: string;
  user_id: string;
  replyAuthor: {
    user_first_name?: string;
    user_email?: string;
    user_profile?: {
      full_name?: string;
      email?: string;
    };
  };
  parentComment: {
    content: string;
    id: string;
  };
}

interface AdminNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  created_at: string;
  is_read: boolean;
}

interface UserNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  created_at: string;
  is_read: boolean;
}

interface NotificationsProps {
  onClose?: () => void;
}

export const Notifications: React.FC<NotificationsProps> = ({ onClose }) => {
  const { user } = useAuth();
  const { hasRole } = useUserRole();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [adminNotifications, setAdminNotifications] = useState<AdminNotification[]>([]);
  const [userNotifications, setUserNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data: userData, error: userNotifError } = await supabaseClient
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (userNotifError) {
        console.error('Error fetching user notifications:', userNotifError);
      } else {
        setUserNotifications(userData || []);
      }

      if (hasRole('super_admin')) {
        const { data: adminData, error: adminError } = await supabaseClient
          .from('admin_notifications')
          .select('*')
          .eq('target_role', 'super_admin')
          .order('created_at', { ascending: false });

        if (adminError) {
          console.error('Error fetching admin notifications:', adminError);
        } else {
          setAdminNotifications(adminData || []);
        }
      }

      // Fetch all comments by the current user to get their IDs
      const { data: myComments, error: myCommentsError } = await supabaseClient
        .from('community_comments')
        .select('id')
        .eq('user_id', user.id);

      if (myCommentsError) {
        console.error('Error fetching my comments:', myCommentsError);
        return;
      }

      if (!myComments || myComments.length === 0) {
        setNotifications([]);
        return;
      }

      const myCommentIds = myComments.map(c => c.id);

      // Fetch all replies to the user's comments
      const { data: replies, error: repliesError } = await supabaseClient
        .from('community_comments')
        .select('*')
        .in('parent_id', myCommentIds)
        .neq('user_id', user.id) // Exclude replies from the user themselves
        .order('created_at', { ascending: false });

      if (repliesError) {
        console.error('Error fetching replies:', repliesError);
        setError('Failed to load notifications');
        return;
      }

      if (!replies || replies.length === 0) {
        setNotifications([]);
        return;
      }

      // Fetch user profiles for reply authors
      const notificationsWithAuthors = await Promise.all(
        replies.map(async (reply) => {
          const { data: profile } = await supabaseClient
            .from('user_profiles')
            .select('full_name, email')
            .eq('id', reply.user_id)
            .single();

          // Fetch the parent comment to show context
          const { data: parentComment } = await supabaseClient
            .from('community_comments')
            .select('content, id')
            .eq('id', reply.parent_id)
            .single();

          return {
            ...reply,
            replyAuthor: {
              user_first_name: reply.user_first_name,
              user_email: reply.user_email,
              user_profile: profile
            },
            parentComment: parentComment || { content: '', id: '' }
          };
        })
      );

      setNotifications(notificationsWithAuthors as Notification[]);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [user, hasRole]);

  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const formatDate = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getReplyAuthorName = (notification: Notification) => {
    return notification.replyAuthor.user_first_name || 
           notification.replyAuthor.user_profile?.full_name?.split(' ')[0] || 
           notification.replyAuthor.user_email?.split('@')[0] || 
           notification.replyAuthor.user_profile?.email?.split('@')[0] || 
           'Someone';
  };

  if (!user) {
    return (
      <div className="notifications-panel">
        <div className="notifications-empty">
          Please sign in to view notifications
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-panel">
      <div className="notifications-header">
        <h3>Notifications</h3>
        {onClose && (
          <button className="notifications-close" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      {error && (
        <div className="notifications-error">
          {error}
        </div>
      )}

      <div className="notifications-content">
        {loading ? (
          <div className="notifications-loading">
            Loading notifications...
          </div>
        ) : notifications.length === 0 && adminNotifications.length === 0 && userNotifications.length === 0 ? (
          <div className="notifications-empty">
            No notifications yet. You'll see replies to your comments here.
          </div>
        ) : (
          <div className="notifications-list">
            {userNotifications.length > 0 && (
              <div className="notification-group">
                <div className="notification-group-title">Your Updates</div>
                {userNotifications.map((notification) => (
                  <div key={notification.id} className="notification-item">
                    <div className="notification-header">
                      <span className="notification-author">{notification.title}</span>
                      <span className="notification-time">
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    <div className="notification-context">
                      <div className="notification-reply">
                        {notification.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {adminNotifications.length > 0 && (
              <div className="notification-group">
                <div className="notification-group-title">Admin Requests</div>
                {adminNotifications.map((notification) => (
                  <div key={notification.id} className="notification-item">
                    <div className="notification-header">
                      <span className="notification-author">{notification.title}</span>
                      <span className="notification-time">
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    <div className="notification-context">
                      <div className="notification-reply">
                        {notification.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {notifications.map(notification => (
              <div key={notification.id} className="notification-item">
                <div className="notification-header">
                  <span className="notification-author">
                    {getReplyAuthorName(notification)} replied to your comment
                  </span>
                  <span className="notification-time">
                    {formatDate(notification.created_at)}
                  </span>
                </div>
                <div className="notification-context">
                  <div className="notification-parent">
                    <strong>Your comment:</strong> {notification.parentComment.content.substring(0, 100)}{notification.parentComment.content.length > 100 ? '...' : ''}
                  </div>
                  <div className="notification-reply">
                    <strong>Reply:</strong> {notification.content}
                  </div>
                </div>
                {notification.chart_context && (
                  <div className="notification-meta">
                    📊 {notification.chart_context}
                    {notification.location_context && ` • 📍 ${notification.location_context}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const { hasRole } = useUserRole();
  const [showPanel, setShowPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const { count: userCount } = await supabaseClient
        .from('user_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      let adminUnreadCount = 0;
      if (hasRole('super_admin')) {
        const { count: adminCount } = await supabaseClient
          .from('admin_notifications')
          .select('*', { count: 'exact', head: true })
          .eq('target_role', 'super_admin')
          .eq('is_read', false);
        adminUnreadCount = adminCount || 0;
      }

      // Fetch all comments by the current user
      const { data: myComments } = await supabaseClient
        .from('community_comments')
        .select('id')
        .eq('user_id', user.id);

      if (!myComments || myComments.length === 0) {
        setUnreadCount((userCount || 0) + adminUnreadCount);
        return;
      }

      const myCommentIds = myComments.map(c => c.id);

      // Count replies to the user's comments
      const { count, error } = await supabaseClient
        .from('community_comments')
        .select('*', { count: 'exact', head: true })
        .in('parent_id', myCommentIds)
        .neq('user_id', user.id);

      if (!error && count !== null) {
        setUnreadCount((userCount || 0) + count + adminUnreadCount);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [user, hasRole]);

  useEffect(() => {
    fetchUnreadCount();
    
    // Refresh count every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    const handleRefresh = () => {
      fetchUnreadCount();
    };

    window.addEventListener('notifications:refresh', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', handleRefresh);
    };
  }, [fetchUnreadCount]);

  if (!user) return null;

  return (
    <>
      <button 
        className="notification-bell-button"
        onClick={async () => {
          const next = !showPanel;
          setShowPanel(next);
          if (next) {
            await supabaseClient
              .from('user_notifications')
              .update({ is_read: true })
              .eq('user_id', user.id)
              .eq('is_read', false);

            if (hasRole('super_admin')) {
              await supabaseClient
                .from('admin_notifications')
                .update({ is_read: true })
                .eq('target_role', 'super_admin')
                .eq('is_read', false);
            }

            fetchUnreadCount();
          }
        }}
        title="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {showPanel && (
        <>
          <div className="notification-overlay" onClick={() => setShowPanel(false)} />
          <div className="notification-dropdown">
            <Notifications onClose={() => setShowPanel(false)} />
          </div>
        </>
      )}
    </>
  );
};

